/**
 * Dashboard Overview Component
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Clock, DollarSign, TrendingUp } from "lucide-react";
import { authAxios } from "@/utils/auth";

export default function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await authAxios.get("/admin/stats");
      setStats(res.data);
    } catch {
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const statCards = [
    { label: "Total Quotes", value: stats?.total_quotes || 0, icon: FileText, color: "blue" },
    { label: "Pending Quotes", value: stats?.pending_quotes || 0, icon: Clock, color: "yellow" },
    { label: "Total Revenue", value: `$${(stats?.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: "emerald" },
  ];

  return (
    <div data-testid="dashboard-overview">
      <h1 className="text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Outfit' }}>
        Dashboard
      </h1>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {loading ? (
          <>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </>
        ) : (
          statCards.map((stat) => (
            <Card key={stat.label} className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 bg-${stat.color}-100 rounded-xl flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-slate-400" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <Link
            to="/admin/quotes"
            className="p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
          >
            <p className="font-medium text-blue-900">View All Quotes</p>
            <p className="text-sm text-blue-600 mt-1">Review and manage customer quotes</p>
          </Link>
          <Link
            to="/admin/pricing"
            className="p-4 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors"
          >
            <p className="font-medium text-emerald-900">Update Pricing</p>
            <p className="text-sm text-emerald-600 mt-1">Configure service prices and discounts</p>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
